import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertCircle,
  Boxes,
  HardHat,
  Pencil,
  Plus,
  Repeat2,
  ScanLine,
  Search,
  Stethoscope,
  Truck,
  Upload,
  UserCheck,
  UserX,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { Asset, AssetStatus, Station } from '@hcc/types';
import { STATIONS, VISIBLE_STATIONS, assetPrimaryLabel, assetRegistration } from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import {
  useAssetsCounts,
  useAssetsList,
  useUnassignAsset,
} from '../../hooks/useAssets';
import { Button } from '../../components/ui/Button';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { ApiError } from '../../lib/api';
import { AssignAssetDialog } from './AssignAssetDialog';

const STATION_META: Record<Station, { label: string; icon: LucideIcon }> = {
  IT: { label: 'IT', icon: ScanLine },
  PPE: { label: 'PPE / Uniform', icon: HardHat },
  Tools: { label: 'Tools', icon: Wrench },
  Fleet: { label: 'Fleet', icon: Truck },
  DBSHR: { label: 'DBS / HR', icon: Stethoscope },
};

const STATUS_PILL: Record<AssetStatus, string> = {
  available: 'bg-surface-2 text-fg-muted',
  assigned: 'bg-warning-soft/15 text-warning-soft-fg',
  issued: 'bg-success-soft/15 text-success-soft-fg',
  retired: 'bg-surface-inset text-fg-muted',
};

const STATUS_LABEL: Record<AssetStatus, string> = {
  available: 'Available',
  assigned: 'Assigned',
  issued: 'Issued',
  retired: 'Retired',
};

function isStation(v: string | null): v is Station {
  return !!v && (STATIONS as readonly string[]).includes(v);
}

export function AssetsList() {
  const { data: user, isLoading: meLoading } = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [assignOpen, setAssignOpen] = useState<Asset | null>(null);
  const unassign = useUnassignAsset();

  const stationParam = params.get('station');
  const includeRetired = params.get('show_retired') === 'true';
  const station: Station = isStation(stationParam) ? stationParam : 'IT';

  const list = useAssetsList({
    station,
    search: search.trim() || undefined,
    include_retired: includeRetired,
  });
  const counts = useAssetsCounts();

  const visibleStations = useMemo<Station[]>(() => {
    if (!user) return [];
    const isCoord = user.roles.some((r) =>
      ['admin', 'super_admin', 'coordinator'].includes(r),
    );
    if (isCoord) return [...VISIBLE_STATIONS];
    const stationByRole: Record<string, Station> = {
      station_it: 'IT',
      station_ppe: 'PPE',
      station_tools: 'Tools',
      station_fleet: 'Fleet',
    };
    return VISIBLE_STATIONS.filter((s) =>
      Object.entries(stationByRole).some(
        ([role, st]) => st === s && user.roles.includes(role),
      ),
    );
  }, [user]);

  if (meLoading)
    return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (visibleStations.length === 0) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        You do not have permission to manage equipment. Ask a coordinator
        for access.
      </main>
    );
  }

  function setStation(next: Station) {
    const sp = new URLSearchParams(params);
    sp.set('station', next);
    setParams(sp, { replace: true });
  }

  function toggleRetired() {
    const sp = new URLSearchParams(params);
    if (includeRetired) sp.delete('show_retired');
    else sp.set('show_retired', 'true');
    setParams(sp, { replace: true });
  }

  async function onUnassign(asset: Asset) {
    try {
      await unassign.mutateAsync(asset.id);
      toast.success(`Unassigned ${asset.asset_tag}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'unassign failed');
    }
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
              <Boxes className="h-6 w-6" strokeWidth={2} />
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-accent">
                Pre-day
              </div>
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                Equipment
              </h1>
              <p className="mt-1 text-sm text-fg-muted">
                Master register of physical assets handed out at each
                station. Pre-assign items to people before the day.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/assets/import">
              <Button
                variant="secondary"
                size="md"
                iconLeft={<Upload className="h-4 w-4" />}
              >
                Import CSV
              </Button>
            </Link>
            <Link to={`/admin/assets/new?station=${station}`}>
              <Button
                variant="primary"
                size="md"
                iconLeft={<Plus className="h-4 w-4" />}
              >
                New asset
              </Button>
            </Link>
          </div>
        </header>

        <div className="mt-6 flex flex-wrap items-center gap-1 border-b border-line/10">
          {visibleStations.map((s) => {
            const Meta = STATION_META[s];
            const isActive = s === station;
            const c = counts.data?.[s];
            return (
              <button
                key={s}
                type="button"
                onClick={() => setStation(s)}
                className={[
                  'group inline-flex items-center gap-2 rounded-t-lg border-b-2 px-3 py-2 text-sm font-medium transition-colors -mb-px',
                  isActive
                    ? 'border-slate-900 text-fg'
                    : 'border-transparent text-fg-muted hover:text-fg',
                ].join(' ')}
              >
                <Meta.icon
                  className={
                    isActive
                      ? 'h-4 w-4 text-fg'
                      : 'h-4 w-4 text-fg-muted group-hover:text-fg-muted'
                  }
                />
                <span>{Meta.label}</span>
                {c ? (
                  <span
                    className={[
                      'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums',
                      isActive
                        ? 'bg-slate-900 text-white'
                        : 'bg-surface-2 text-fg-muted',
                    ].join(' ')}
                  >
                    {/* Match the list: retired items are hidden unless the
                        "Show retired" toggle is on, so exclude them from the
                        badge too or the count contradicts an empty list. */}
                    {includeRetired ? c.total : c.total - c.retired}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[16rem] flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tag, description, reg, serial..."
              className="block h-9 w-full rounded-md border border-line-strong/20 bg-surface pl-8 pr-3 text-sm shadow-sm transition-colors focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong focus-visible:ring-offset-1"
            />
          </div>
          <label className="inline-flex items-center gap-1.5 rounded-md border border-line/10 bg-surface px-2.5 py-1.5 text-xs text-fg-muted">
            <input
              type="checkbox"
              checked={includeRetired}
              onChange={toggleRetired}
              className="h-3.5 w-3.5"
            />
            Show retired
          </label>
        </div>

        <div className="mt-3 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          {list.isLoading ? (
            <SkeletonList rows={4} />
          ) : list.error ? (
            <EmptyState
              icon={<AlertCircle className="h-6 w-6" />}
              title="Couldn't load assets"
              description={(list.error as Error).message}
            />
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              icon={<Boxes className="h-6 w-6" />}
              title={`No ${STATION_META[station].label} assets yet`}
              description="Bulk-import a CSV or create one manually to get started."
              action={
                <div className="flex flex-wrap gap-2">
                  <Link to="/admin/assets/import">
                    <Button
                      variant="secondary"
                      iconLeft={<Upload className="h-4 w-4" />}
                    >
                      Import CSV
                    </Button>
                  </Link>
                  <Link to={`/admin/assets/new?station=${station}`}>
                    <Button
                      variant="primary"
                      iconLeft={<Plus className="h-4 w-4" />}
                    >
                      New asset
                    </Button>
                  </Link>
                </div>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs uppercase tracking-wider text-fg-muted">
                  <tr>
                    <th className="px-4 py-2.5">
                      {station === 'Fleet' ? 'Registration' : 'Asset tag'}
                    </th>
                    <th className="px-4 py-2.5">Description</th>
                    <th className="px-4 py-2.5">Category</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Assigned to</th>
                    <th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((a) => (
                    <AssetRow
                      key={a.id}
                      asset={a}
                      onAssignOrReassign={() => setAssignOpen(a)}
                      onUnassign={() => onUnassign(a)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {assignOpen ? (
        <AssignAssetDialog
          asset={assignOpen}
          onClose={() => setAssignOpen(null)}
        />
      ) : null}
    </main>
  );
}

function AssetRow({
  asset,
  onAssignOrReassign,
  onUnassign,
}: {
  asset: Asset;
  onAssignOrReassign: () => void;
  onUnassign: () => void;
}) {
  // For a Fleet vehicle with a plate, lead with the registration and keep the
  // asset tag as the secondary line; otherwise tag over CAFM syscode as before.
  const showsReg = asset.station === 'Fleet' && assetRegistration(asset) !== null;
  const secondary = showsReg ? asset.asset_tag : asset.syscode;
  return (
    <tr className="border-t border-line/10 transition-colors hover:bg-surface-2/50">
      <td className="px-4 py-2.5 font-mono text-xs font-medium">
        {assetPrimaryLabel(asset)}
        {secondary ? (
          <span
            className="mt-0.5 block font-normal text-fg-dim"
            title={showsReg ? 'Asset tag' : 'CAFM syscode'}
          >
            {secondary}
          </span>
        ) : null}
      </td>
      <td className="px-4 py-2.5">{asset.description}</td>
      <td className="px-4 py-2.5 text-fg-muted">{asset.category ?? '—'}</td>
      <td className="px-4 py-2.5">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_PILL[asset.status]}`}
        >
          {STATUS_LABEL[asset.status]}
        </span>
      </td>
      <td className="px-4 py-2.5">
        {asset.assigned_to_person_id ? (
          <Link
            to={`/admin/people/${asset.assigned_to_person_id}`}
            className="text-xs font-medium text-fg underline-offset-2 hover:underline"
            title="Open person record"
          >
            {asset.assigned_to_full_name ?? 'Unknown person'}
          </Link>
        ) : (
          <span className="text-xs text-fg-dim">—</span>
        )}
      </td>
      <td className="px-4 py-2.5 text-right">
        <div className="inline-flex flex-wrap justify-end gap-1">
          {asset.status === 'assigned' ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<Repeat2 className="h-3.5 w-3.5" />}
                onClick={onAssignOrReassign}
                title="Pick a different person"
              >
                Reassign
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<UserX className="h-3.5 w-3.5" />}
                onClick={onUnassign}
                title="Send back to the pool"
              >
                Unassign
              </Button>
            </>
          ) : asset.status === 'available' ? (
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<UserCheck className="h-3.5 w-3.5" />}
              onClick={onAssignOrReassign}
            >
              Assign
            </Button>
          ) : null}
          <Link to={`/admin/assets/${asset.id}`}>
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<Pencil className="h-3.5 w-3.5" />}
            >
              Edit
            </Button>
          </Link>
        </div>
      </td>
    </tr>
  );
}
